import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Form primitives, vendored.
 *
 * Under Soft Pastel an input is a sunken well rather than a bordered box: a
 * panel fill, no rule, and a rule that appears on focus as an inset ring. The
 * surface has few enough lines that adding one per field would be the loudest
 * thing on the screen.
 */

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(
        'h-11 w-full rounded-full border-0 bg-bone px-[18px] text-[0.9375rem] text-graphite-900',
        'placeholder:text-graphite-300 focus:outline-none focus:shadow-[inset_0_0_0_1.5px_var(--rule-strong)]',
        'disabled:cursor-not-allowed disabled:text-graphite-300',
        className,
      )}
      {...props}
    />
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(
          'w-full resize-none rounded-md border-0 bg-bone px-[18px] py-3.5 text-[0.9375rem] leading-[1.55] text-graphite-900',
          'placeholder:text-graphite-300 focus:outline-none focus:shadow-[inset_0_0_0_1.5px_var(--rule-strong)]',
          'disabled:cursor-not-allowed disabled:text-graphite-300',
          className,
        )}
        {...props}
      />
    );
  },
);

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="eyebrow block">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-sm text-alarm">{error}</p>
      ) : hint ? (
        <p className="text-sm text-graphite-500">{hint}</p>
      ) : null}
    </div>
  );
}
